<template>
    <NoteText text="You can make notes on the form below" />
    <div class="note">C = Correct, I = Incorrect, R = Repeat, Q = Query</div>

    <!-- Transcription status indicator -->
    <div v-if="isTranscribing" class="transcription-status">
        <div class="transcribing-indicator">Transcribing audio...</div>
    </div>
    <div v-if="transcriptionError" class="transcription-error">
        {{ transcriptionError }}
    </div>

    <div v-if="jsonData" class="text-input-grid">
        <div v-for="(item, index) in jsonData.entries" :key="item.entryId" class="input-row">
            <span class="input-number">{{ index + 1 }}.</span>
            <div class="input-container">
                <TextInputWithToggle 
                    :text="item.enteredWord"
                    :selectedOption="item.selectedAnswer"
                    @update:text="updateEnteredWord(index, $event)"
                    @update:selectedOption="updateSelectedAnswer(index, $event)"
                    @focus="handleInputFocus(index)"
                    @blur="handleInputBlur(index)"
                    @keydown="handleKeyDown($event, index)"
                    ref="textInputs"
                />
                <!-- Add AI badge if this is an applied suggestion -->
                <span v-if="isSuggestionApplied(index)" class="ai-badge">AI</span>
                <!-- Add suggestion overlay if there's a suggestion -->
                <div 
                    v-if="hasSuggestion(index) && !isSuggestionApplied(index) && !typingIndices.has(index)" 
                    class="suggestion-overlay"
                    @click="handleSuggestionClick(index)"
                >
                    <span v-if="focusedIndex === index" class="suggestion-text">
                        Press Tab to use: {{ truncateSuggestion(getSuggestion(index)) }}
                    </span>
                    <span v-else class="suggestion-text">
                        Suggestion: {{ truncateSuggestion(getSuggestion(index)) }}
                    </span>
                </div>
            </div>
        </div>
    </div>
    
    <div class="summary-section">
        <h3>Entry Summary</h3>
        <div class="summary-grid">
            <div class="summary-item">
                <span class="summary-label">Correct (C):</span>
                <span class="summary-count">{{ getSummaryValue('c') }}</span>
            </div>
            <div class="summary-item">
                <span class="summary-label">Incorrect (I):</span>
                <span class="summary-count">{{ getSummaryValue('i') + getSummaryValue('ir') }}</span>
            </div>
            <div class="summary-item">
                <span class="summary-label">Repeat (R):</span>
                <span class="summary-count">{{ getSummaryValue('r') + getSummaryValue('ir')}}</span>
            </div>
            <div class="summary-item">
                <span class="summary-label">Query (Q):</span>
                <span class="summary-count">{{ getSummaryValue('q') }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import TextInputWithToggle from '../TextInputWithToggle.vue';
import NoteText from '../NoteText.vue';
import OpenAIService from '../../services/OpenAIService';

export default {
    components: {
        TextInputWithToggle,
        NoteText
    },
    props: {
        jsonData: {
            type: Object,
            default: null
        },
        audioFile: {
            type: Object,
            default: null
        }
    },
    emits: ['update:enteredWord', 'update:selectedAnswer', 'entrySummaryUpdated', 'update:suggestedWords'],
    data() {
        return {
            isTranscribing: false,
            transcriptionError: null,
            transcriptionSuggestions: [],
            appliedSuggestions: new Set(),
            originalSuggestions: new Map(),
            focusedIndex: null,
            typingIndices: new Set(), // Track which inputs have user typing
            typingTimeouts: {} // Track typing timeouts
        };
    },
    watch: {
        // Watch for changes to the audioFile prop
        audioFile: {
            immediate: true,
            handler(newFile) {
                if (newFile) {
                    this.transcribeAudioFile(newFile);
                }
            }
        },
        // Watch for changes to jsonData to load suggestions
        jsonData: {
            immediate: true,
            handler(newData) {
                if (newData && newData.suggestedWords && newData.suggestedWords.length > 0) {
                    this.loadSuggestionsFromData(newData.suggestedWords);
                }
            }
        }
    },
    mounted() {
        // Load suggestions from jsonData if available
        if (this.jsonData && this.jsonData.suggestedWords && this.jsonData.suggestedWords.length > 0) {
            this.loadSuggestionsFromData(this.jsonData.suggestedWords);
        }
    },
    methods: {
        // Load suggestions from saved data
        loadSuggestionsFromData(words) {
            if (Array.isArray(words) && words.length > 0) {
                this.transcriptionSuggestions = words;
                console.log('Loaded suggestions from data:', words);
                
                // Reset applied suggestions when loading new data
                this.appliedSuggestions = new Set();
                this.originalSuggestions = new Map();
                
                // We don't automatically mark entries as suggestions anymore
                // Let the user explicitly apply suggestions via tab
            }
        },
        updateEnteredWord(index, text) {
            if (this.jsonData && this.jsonData.entries && this.jsonData.entries[index]) {
                // Check if this was a suggestion that's now being modified
                if (this.appliedSuggestions.has(index)) {
                    const originalSuggestion = this.originalSuggestions.get(index);
                    // If the text is different from the original suggestion, remove the badge
                    if (text !== originalSuggestion) {
                        this.appliedSuggestions.delete(index);
                    }
                }
                
                // Emit the update event instead of directly modifying the prop
                this.$emit('update:enteredWord', index, text);
                
                // Mark this index as typing
                this.typingIndices.add(index);
                
                // Clear typing status after a delay
                clearTimeout(this.typingTimeouts[index]);
                this.typingTimeouts[index] = setTimeout(() => {
                    this.typingIndices.delete(index);
                }, 500);
            }
        },
        updateSelectedAnswer(index, value) {
            this.$emit('update:selectedAnswer', index, value);
        },
        getSummaryValue(key) {
            if (this.jsonData && this.jsonData.entrySummary) {
                return this.jsonData.entrySummary[key] || 0;
            }
            return 0;
        },
        // Transcribe the audio file automatically when it's provided
        async transcribeAudioFile(file) {
            if (!file) return;
            
            this.isTranscribing = true;
            this.transcriptionError = null;
            
            try {
                const transcriptionText = await OpenAIService.transcribeAudio(file);
                console.log('Transcription result:', transcriptionText);
                
                // Process the transcription into individual words
                this.processTranscription(transcriptionText);
            } catch (error) {
                console.error('Transcription error:', error);
                this.transcriptionError = `Error transcribing audio: ${error.message || 'Unknown error'}`;
            } finally {
                this.isTranscribing = false;
            }
        },
        // Process the transcription text into individual words
        processTranscription(text) {
            if (!text) return;
            
            // Split the text into words, clean them, and filter out empty strings
            const words = text.split(/\s+/)
                .map(word => word.trim().toLowerCase())
                .filter(word => word.length > 0);
            
            console.log('Processed words:', words);
            
            // Store the words as suggestions
            this.transcriptionSuggestions = words;
            
            // Reset applied suggestions
            this.appliedSuggestions = new Set();
            this.typingIndices = new Set();
            
            // Emit event to update suggestedWords in parent component
            this.$emit('update:suggestedWords', words);
        },
        // Check if there's a suggestion for a specific index
        hasSuggestion(index) {
            // Don't show suggestion if:
            // 1. There's no suggestion at this index, or
            // 2. User is currently typing, or
            // 3. The input already has text entered
            return index < this.transcriptionSuggestions.length && 
                   this.transcriptionSuggestions[index] && 
                   !this.typingIndices.has(index) &&
                   this.jsonData && 
                   this.jsonData.entries && 
                   this.jsonData.entries[index] && 
                   !this.jsonData.entries[index].enteredWord;
        },
        // Get the suggestion for a specific index
        getSuggestion(index) {
            return this.transcriptionSuggestions[index] || '';
        },
        // Check if a suggestion has been applied
        isSuggestionApplied(index) {
            // Only return true if the suggestion has been explicitly applied
            // from the transcription suggestions, not just if there's text
            return this.appliedSuggestions.has(index);
        },
        // Apply a suggestion to an entry
        applySuggestion(index) {
            if (this.hasSuggestion(index) && !this.isSuggestionApplied(index)) {
                const suggestion = this.getSuggestion(index);
                this.updateEnteredWord(index, suggestion);
                this.appliedSuggestions.add(index);
                // Store the original suggestion text
                this.originalSuggestions.set(index, suggestion);
            }
        },
        // Handle input focus
        handleInputFocus(index) {
            this.focusedIndex = index;
        },
        // Handle input blur
        handleInputBlur(index) {
            if (this.focusedIndex === index) {
                this.focusedIndex = null;
            }
        },
        // Handle key down events
        handleKeyDown(event, index) {
            // If Tab key is pressed and there's a suggestion
            if (event.key === 'Tab' && this.hasSuggestion(index) && !this.isSuggestionApplied(index)) {
                event.preventDefault(); // Prevent default tab behavior
                this.applySuggestion(index);
                
                // Focus the next input if available
                this.$nextTick(() => {
                    const nextIndex = index + 1;
                    if (this.$refs.textInputs && this.$refs.textInputs[nextIndex]) {
                        this.$refs.textInputs[nextIndex].focus();
                    }
                });
            }
        },
        // Handle suggestion click
        handleSuggestionClick(index) {
            // Focus the input
            if (this.$refs.textInputs && this.$refs.textInputs[index]) {
                this.$refs.textInputs[index].focus();
            }
        },
        truncateSuggestion(suggestion) {
            if (!suggestion) return '';
            // Limit suggestion to a reasonable length (adjust as needed)
            const maxLength = 25;
            return suggestion.length > maxLength 
                ? suggestion.substring(0, maxLength) + '...' 
                : suggestion;
        }
    }
};
</script>

<style scoped>
.text-input-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    row-gap: 10px;
    column-gap: 20px;
}

@media (max-width: 1024px) {
    .text-input-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .text-input-grid {
        grid-template-columns: 1fr;
    }

}

.input-row {
    display: flex;
    align-items: center;
    margin-bottom: 10px;

}

.input-number {
    width: 30px;
    font-weight: bold;
    text-align: right;
    margin-right: 10px;
}

.input-container {
    position: relative;
    flex-grow: 1;
}

.suggestion-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    padding: 0;
    color: #777;
    font-style: italic;
    cursor: pointer;
    opacity: 0.9;
    z-index: 1;
    border-radius: 4px;
    pointer-events: none; /* Allow clicks to pass through to the input */
}

.suggestion-text {
    padding: 10px 12px;
    line-height: 1.5;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.note {
    margin-left: 25px;
    margin-bottom: 30px;
}

.transcription-status {
    margin-bottom: 20px;
}

.transcribing-indicator {
    color: #3498db;
    font-style: italic;
}

.transcription-error {
    color: #e74c3c;
    margin-top: 5px;
    margin-bottom: 15px;
    font-size: 14px;
}

.summary-section {
    margin-top: 30px;
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #f9f9f9;
}

.summary-section h3 {
    margin-top: 0;
    color: #237091;
    margin-bottom: 15px;
}

.summary-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
}

.summary-item {
    display: flex;
    align-items: center;
    min-width: 150px;
}

.summary-label {
    font-weight: bold;
    margin-right: 10px;
}

.summary-count {
    font-size: 18px;
    color: #237091;
}

.ai-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: #237091;
    color: white;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 10px;
    font-weight: bold;
    z-index: 2;
}
</style>
